import { useEffect, useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import { Modal } from './Modal'
import { UIButton } from './UIButton'
import { FormField } from './FormField'

export interface InputDialogFieldSpec {
  /** Key used in the resolved values object. */
  name: string
  label: string
  placeholder?: string
  required?: boolean
  /** Render a multi-line textarea. */
  multiline?: boolean
  type?: 'text' | 'tel' | 'number' | 'email'
  defaultValue?: string
  autoFocus?: boolean
}

export interface InputDialogOptions {
  title: string
  message?: string
  fields: InputDialogFieldSpec[]
  confirmLabel?: string
  cancelLabel?: string
}

export interface InputDialogProps extends InputDialogOptions {
  open: boolean
  onSubmit: (values: Record<string, string>) => void
  onCancel: () => void
}

/**
 * Controlled prompt-style dialog (KAN-1179) supporting one or MORE fields — the
 * multi-field form (recipient / OTP / notes) that replaces the stacked
 * `window.prompt()` proof-of-delivery capture in the delivery console. Built on Modal
 * so it is mobile-friendly, focus-trapped, and Esc-dismissible. Prefer the
 * `useInputDialog()` hook (DialogProvider) for imperative use.
 */
export function InputDialog({
  open,
  title,
  message,
  fields,
  confirmLabel = 'Submit',
  cancelLabel = 'Cancel',
  onSubmit,
  onCancel
}: InputDialogProps) {
  const initialValues = useMemo(() => {
    const seed: Record<string, string> = {}
    fields.forEach(field => {
      seed[field.name] = field.defaultValue ?? ''
    })
    return seed
  }, [fields])

  const [values, setValues] = useState<Record<string, string>>(initialValues)
  const [touched, setTouched] = useState(false)

  // Reset when the dialog (re)opens so a reused instance never shows stale input.
  useEffect(() => {
    if (open) {
      setValues(initialValues)
      setTouched(false)
    }
  }, [open, initialValues])

  const missingRequired = fields.some(field => field.required && !values[field.name]?.trim())

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setTouched(true)
    if (missingRequired) return
    const trimmed: Record<string, string> = {}
    Object.entries(values).forEach(([key, value]) => {
      trimmed[key] = value.trim()
    })
    onSubmit(trimmed)
  }

  return (
    <Modal open={open} onClose={onCancel} title={title} description={message} size="sm" ariaLabel={title}>
      <form onSubmit={handleSubmit} className="ui-input-dialog__form" noValidate>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          {fields.map((field, index) => {
            const value = values[field.name] ?? ''
            const showError = touched && field.required && !value.trim()
            const commonProps = {
              name: field.name,
              placeholder: field.placeholder,
              value,
              autoFocus: field.autoFocus ?? index === 0,
              onChange: (event: { target: { value: string } }) =>
                setValues(current => ({ ...current, [field.name]: event.target.value }))
            }
            return (
              <FormField
                key={field.name}
                label={field.label}
                required={field.required}
                error={showError ? `${field.label} is required` : undefined}
              >
                {field.multiline ? (
                  <textarea rows={3} {...commonProps} />
                ) : (
                  <input type={field.type ?? 'text'} {...commonProps} />
                )}
              </FormField>
            )
          })}
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 'var(--space-3)',
            marginTop: 'var(--space-6)',
            flexWrap: 'wrap'
          }}
        >
          <UIButton variant="secondary" onClick={onCancel} type="button">
            {cancelLabel}
          </UIButton>
          <UIButton variant="primary" type="submit" disabled={touched && missingRequired}>
            {confirmLabel}
          </UIButton>
        </div>
      </form>
    </Modal>
  )
}

export default InputDialog
